import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { DocumentType } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Activity } from '../../../common/models/activity';
import { DocumentStreamEntry, DocumentStreamKind } from '../../../common/models/document-stream-entry';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** Matches the cap the activity endpoint used to have — one screenful and then some. */
const PAGE_SIZE = 30;

export interface DocumentStreamState {
    entries: DocumentStreamEntry[];
    total: number;
    loading: boolean;
    /** True once every page of the current filter is loaded. */
    complete: boolean;
    kind?: DocumentStreamKind;
    setKind: (kind?: DocumentStreamKind) => void;
    /** Only in chain mode: the document kind the stream is narrowed to. */
    stage?: string;
    setStage: (stage?: string) => void;
    loadMore: () => void;
    reload: () => void;
    /**
     * Puts a changed activity back into the stream. Used after a comment was
     * written, so that the pages already loaded stay where they are instead of
     * being fetched again.
     */
    replaceActivity: (activity: Activity) => void;
}

/**
 * Loads the document stream page by page (#2154).
 *
 * Deliberately not in the store: the stream belongs to one open document and to
 * one panel, and the filter and the loaded pages are its own state. The
 * activity and transition slices stay untouched for the panels that still read
 * them.
 */
export const useDocumentStream = (
    documentType: DocumentType,
    documentId: number | undefined,
    /**
     * Reads the stream of the whole procurement instead of this one document
     * (#2160). The paging, the race guard and the filters are the same
     * question either way — only the endpoint differs.
     */
    chain = false
): DocumentStreamState => {
    const dispatch = useThunkDispatch();
    const activityLoadCount = useAppSelector(state => state.activities.loadCount);
    const [entries, setEntries] = useState<DocumentStreamEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [kind, setKind] = useState<DocumentStreamKind | undefined>(undefined);
    const [stage, setStage] = useState<string | undefined>(undefined);
    // Answers arriving out of order must not append to a list they no longer
    // belong to — a slow first page landing after a filter change would.
    const requestRef = useRef(0);

    const load = useCallback(
        async (targetPage: number, targetKind?: DocumentStreamKind, targetStage?: string) => {
            if (!documentId) {
                return;
            }

            const requestId = ++requestRef.current;
            setLoading(true);

            const response = await dispatch((_dispatch, _getState, { api }) =>
                chain
                    ? api.activities.getDocumentChainStream(
                          documentType,
                          documentId,
                          targetPage,
                          PAGE_SIZE,
                          targetKind,
                          targetStage
                      )
                    : api.activities.getDocumentStream(documentType, documentId, targetPage, PAGE_SIZE, targetKind)
            );

            if (requestId !== requestRef.current) {
                return;
            }

            setLoading(false);

            if (!response.success || !response.result) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
                return;
            }

            const result = response.result;

            setEntries(previous => (targetPage === 1 ? result.entries : [...previous, ...result.entries]));
            setTotal(result.total);
            setPages(result.pages);
            setPage(result.page);
        },
        [chain, dispatch, documentId, documentType]
    );

    // A new filter is a new stream, so it starts at its first page. The same
    // goes for a reload of the activity log: the masks trigger that after a
    // save, which is exactly when the document gained history.
    useEffect(() => {
        setEntries([]);
        setTotal(0);
        setPage(1);
        load(1, kind, stage);
    }, [activityLoadCount, kind, load, stage]);

    const loadMore = useCallback(() => {
        if (!loading && page < pages) {
            load(page + 1, kind, stage);
        }
    }, [kind, load, loading, page, pages, stage]);

    const reload = useCallback(() => load(1, kind, stage), [kind, load, stage]);

    const replaceActivity = useCallback((activity: Activity) => {
        setEntries(previous =>
            previous.map(entry => {
                if ('activity' !== entry.source || entry.activity?.id !== activity.id) {
                    return entry;
                }

                return Object.assign(new DocumentStreamEntry(), entry, { activity });
            })
        );
    }, []);

    return {
        entries,
        total,
        loading,
        complete: page >= pages,
        kind,
        setKind,
        stage,
        setStage,
        loadMore,
        reload,
        replaceActivity,
    };
};
